import React, { useEffect, useState, useRef } from 'react';
import { Form, FormControl } from 'react-bootstrap';
import NumberInput from '../Generic/NumberInput';
import Button from 'react-bootstrap/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { logInfo } from '../../config_and_helpers/logger';
import { formatNumericInput } from '../../utils/numberInput';
import StoragePlaceSelect from '../Product/StoragePlaceSelect';

/**
 * OrderItemLotComponent
 *
 * Represents a component for displaying and editing lot details within an order item.
 * Handles individual lot tracking including batch numbers, quantities, and expiry dates.
 *
 * @param {Object} props - The component props
 * @param {number} props.orderItemIndex - The index of the parent order item in the order items array
 * @param {number} props.lotIndex - The index of this lot item within the order item's lot items array
 * @param {function} props.onLotChange - Callback function to handle changes to lot details
 * @param {boolean} props.showRemoveButton - Determines if the remove lot button should be displayed
 * @param {function} props.handleLotItemDelete - Callback function to remove this lot from the order item
 * @param {Object} props.formik - Formik object containing form state, validation, and helper methods
 * @param {Array} [props.storageNodes] - Lab storage tree. When set, this lot can pick any storage node.
 * @param {Array<{ value: string, label: string }>|null} [props.storagePlaceOptions] - Shared place options.
 *
 * @returns {JSX.Element} A form section for managing a single lot item entry
 */
const OrderItemLotComponent = ({
  orderItemIndex,
  lotIndex,
  onLotChange,
  showRemoveButton,
  handleLotItemDelete,
  formik,
  allReceivedOK = false,
  lotItem: lotItemFromParent = null,
  storageNodes = null,
  storagePlaceOptions = null,
}) => {
  // State hook for managing delay in updating form field changes.
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [expiryInputValue, setExpiryInputValue] = useState('');
  const [isExpiryInputDirty, setIsExpiryInputDirty] = useState(false);
  const [quantityDraft, setQuantityDraft] = useState('');
  const [isQuantityDirty, setIsQuantityDirty] = useState(false);
  const quantityDraftRef = useRef('');

  // Field names for Formik form fields
  const batchFieldName = `items[${orderItemIndex}].lotItems[${lotIndex}].batch`;
  const expiryFieldName = `items[${orderItemIndex}].lotItems[${lotIndex}].expiry`;
  const lotItemQuantityFieldName = `items[${orderItemIndex}].lotItems[${lotIndex}].lotItemQuantity`;

  const formExpiryValue =
    formik.values?.items?.[orderItemIndex]?.lotItems?.[lotIndex]?.expiry || '';

  const formLot = formik.values?.items?.[orderItemIndex]?.lotItems?.[lotIndex];
  const persistedQuantity =
    allReceivedOK && lotItemFromParent != null
      ? String(lotItemFromParent.lotItemQuantity ?? '')
      : String(formLot?.lotItemQuantity ?? formLot?.lot_item_quantity ?? '');

  const formatIsoToDisplay = isoDateString => {
    if (!isoDateString || !/^\d{4}-\d{2}-\d{2}$/.test(isoDateString)) return '';
    const [year, month, day] = isoDateString.split('-');
    return `${day}/${month}/${year}`;
  };

  const convertDateToIso = date => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseManualDateToIso = rawValue => {
    const trimmed = (rawValue || '').trim();
    if (trimmed === '') return '';
    const match = trimmed.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
    if (!match) return null;
    const [, dayRaw, monthRaw, yearRaw] = match;
    const day = Number(dayRaw);
    const month = Number(monthRaw);
    const year = Number(yearRaw);
    const normalizedDate = new Date(year, month - 1, day);
    const isValid =
      normalizedDate.getFullYear() === year &&
      normalizedDate.getMonth() === month - 1 &&
      normalizedDate.getDate() === day;
    if (!isValid) return null;
    return `${yearRaw}-${monthRaw}-${dayRaw}`;
  };

  const commitExpiryValue = isoValue => {
    formik.setFieldValue(expiryFieldName, isoValue);
    handleLotInstantChange('expiry', isoValue);
    if (process.env.NODE_ENV === 'development') {
      logInfo('OrderItemLotComponent', 'Committed lot expiry value', {
        orderItemIndex,
        lotIndex,
        expiryFieldName,
        isoValue,
      });
    }
  };

  const commitManualExpiryInput = () => {
    const parsedIso = parseManualDateToIso(expiryInputValue);
    if (parsedIso === null) {
      setExpiryInputValue(formatIsoToDisplay(formExpiryValue));
      setIsExpiryInputDirty(false);
      if (process.env.NODE_ENV === 'development') {
        logInfo('OrderItemLotComponent', 'Rejected invalid manual lot expiry', {
          orderItemIndex,
          lotIndex,
          expiryFieldName,
          typedValue: expiryInputValue,
          persistedValue: formExpiryValue,
        });
      }
      return;
    }

    commitExpiryValue(parsedIso);
    setExpiryInputValue(formatIsoToDisplay(parsedIso));
    setIsExpiryInputDirty(false);
  };

  useEffect(() => {
    if (!isExpiryInputDirty) {
      setExpiryInputValue(formatIsoToDisplay(formExpiryValue));
    }
  }, [formExpiryValue, isExpiryInputDirty]);

  // Parent replacements (Mark all OK) update the field only when it is not being edited.
  useEffect(() => {
    if (isQuantityDirty) return;
    quantityDraftRef.current = persistedQuantity;
    setQuantityDraft(persistedQuantity);
  }, [persistedQuantity, isQuantityDirty]);

  /**
   * Write Batch Quantity once, after the user leaves the field.
   * Typing stays local so the reception form is not rebuilt on each digit.
   *
   * @param {string} raw
   */
  const commitLotQuantity = raw => {
    const trimmed = String(raw ?? '').trim();
    const next =
      trimmed === ''
        ? ''
        : formatNumericInput(trimmed, { integerOnly: true }) || trimmed;
    quantityDraftRef.current = next;
    setQuantityDraft(next);
    setIsQuantityDirty(false);
    formik.setFieldValue(lotItemQuantityFieldName, next);
    onLotChange(orderItemIndex, 'lotItemQuantity', next, lotIndex);
    if (process.env.NODE_ENV === 'development') {
      logInfo('OrderItemLotComponent', 'Committed lot quantity', {
        orderItemIndex,
        lotIndex,
        quantity: next,
      });
    }
  };

  // Handler for lot item inputs that has delay in updating their changes.
  // It clears the previously set timeout and sets a new one.
  const handleLotDelayedChange = (name, value) => {
    if (typingTimeout) clearTimeout(typingTimeout);

    const newTimeout = setTimeout(() => {
      onLotChange(orderItemIndex, name, value, lotIndex);
    }, 300);

    setTypingTimeout(newTimeout);
  };

  // Handler for lot item inputs that should update their changes instantly.
  const handleLotInstantChange = (name, value) => {
    onLotChange(orderItemIndex, name, value, lotIndex);
  };

  return (
    <div className="order-lot-panel">
      <h5 className="order-lot-panel__heading">{`Batch #${lotIndex + 1}`}</h5>
      {/* Same pattern is followed for batch input field*/}
      <Form.Group controlId={`itemBatch${lotIndex}`} className="field-margin">
        <Form.Label>Batch Number</Form.Label>
        <FormControl
          name={batchFieldName}
          type="text"
          value={
            formik.values?.items[orderItemIndex]?.lotItems?.[lotIndex]?.batch ||
            ''
          }
          onChange={event => {
            formik.handleChange(event);
            const { value } = event.target;
            handleLotDelayedChange('batch', value);
          }}
          onBlur={formik.handleBlur}
          onFocus={() => formik.setFieldTouched(batchFieldName, true)}
          isValid={
            formik?.touched?.items?.[orderItemIndex]?.lotItems?.[lotIndex]
              ?.batch &&
            !formik?.errors?.items?.[orderItemIndex]?.lotItems?.[lotIndex]
              ?.batch &&
            formik.values?.items?.[orderItemIndex]?.lotItems?.[lotIndex]
              ?.batch !== ''
          }
          isInvalid={
            formik?.touched?.items?.[orderItemIndex]?.lotItems?.[lotIndex]
              ?.batch &&
            !!formik?.errors?.items?.[orderItemIndex]?.lotItems?.[lotIndex]
              ?.batch
          }
        />
        <Form.Control.Feedback type="invalid">
          {!!formik?.errors?.items?.[orderItemIndex]?.lotItems &&
            formik?.errors?.items?.[orderItemIndex]?.lotItems.length >
              lotIndex &&
            formik.errors.items?.[orderItemIndex]?.lotItems?.[lotIndex]?.batch}
        </Form.Control.Feedback>
      </Form.Group>

      {/* Similar structure for the expiry date field */}
      <Form.Group
        controlId={`expiry${orderItemIndex}`}
        className="field-margin"
      >
        <Form.Label>Expiry Date</Form.Label>
        {/* Props for FormControl */}
        <DatePicker
          selected={
            !isExpiryInputDirty && formExpiryValue
              ? new Date(formExpiryValue)
              : null
          }
          value={expiryInputValue}
          onChange={date => {
            const isoDateValue = convertDateToIso(date);
            commitExpiryValue(isoDateValue);
            setExpiryInputValue(formatIsoToDisplay(isoDateValue));
            setIsExpiryInputDirty(false);
          }}
          onChangeRaw={event => {
            setIsExpiryInputDirty(true);
            setExpiryInputValue(event.target.value);
          }}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commitManualExpiryInput();
            }
          }}
          dateFormat="dd/MM/yyyy"
          placeholderText="dd/mm/yyyy"
          className={`form-control ${
            formik?.touched?.items?.[orderItemIndex]?.lotItems?.[lotIndex]
              ?.expiry &&
            !!formik?.errors?.items?.[orderItemIndex]?.lotItems?.[lotIndex]
              ?.expiry
              ? 'is-invalid'
              : formik?.touched?.items?.[orderItemIndex]?.lotItems?.[lotIndex]
                  ?.expiry &&
                !formik?.errors?.items?.[orderItemIndex]?.lotItems?.[lotIndex]
                  ?.expiry &&
                formik.values?.items[orderItemIndex]?.lotItems?.[lotIndex]
                  ?.expiry !== ''
              ? 'is-valid'
              : ''
          }`}
          wrapperClassName="w-100"
          inputClassName="text-center"
          onFocus={() => formik.setFieldTouched(expiryFieldName, true)}
          onBlur={event => {
            formik.handleBlur(event);
            commitManualExpiryInput();
          }}
          showYearDropdown
          showMonthDropdown
          dropdownMode="select"
          todayButton="Today"
        />
        <Form.Control.Feedback type="invalid">
          {!!formik?.errors?.items?.[orderItemIndex]?.lotItems &&
            formik?.errors?.items?.[orderItemIndex]?.lotItems.length >
              lotIndex &&
            formik.errors.items?.[orderItemIndex]?.lotItems?.[lotIndex]?.expiry}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group
        controlId={`lotItemQuantity${lotIndex}`}
        className="field-margin"
      >
        <Form.Label>Batch Quantity</Form.Label>
        <NumberInput
          name={lotItemQuantityFieldName}
          value={quantityDraft}
          integerOnly
          onChange={event => {
            const next = event.target.value;
            quantityDraftRef.current = next;
            setQuantityDraft(next);
            setIsQuantityDirty(true);
          }}
          onBlur={event => {
            commitLotQuantity(quantityDraftRef.current);
            formik.handleBlur(event);
          }}
          isValid={
            formik?.touched?.items?.[orderItemIndex]?.lotItems?.[lotIndex]
              ?.lotItemQuantity &&
            !formik?.errors?.items?.[orderItemIndex]?.lotItems?.[lotIndex]
              ?.lotItemQuantity &&
            formik.values?.items[orderItemIndex]?.lotItems?.[lotIndex]
              ?.lotItemQuantity !== ''
          }
          isInvalid={
            formik?.touched?.items?.[orderItemIndex]?.lotItems?.[lotIndex]
              ?.lotItemQuantity &&
            !!formik?.errors?.items?.[orderItemIndex]?.lotItems?.[lotIndex]
              ?.lotItemQuantity
          }
        />
        <Form.Control.Feedback type="invalid">
          {!!formik?.errors?.items?.[orderItemIndex]?.lotItems &&
            formik?.errors?.items?.[orderItemIndex]?.lotItems.length >
              lotIndex &&
            formik.errors.items?.[orderItemIndex]?.lotItems?.[lotIndex]
              ?.lotItemQuantity}
        </Form.Control.Feedback>
      </Form.Group>
      {storageNodes ? (
        <Form.Group className="field-margin">
          <StoragePlaceSelect
            nodes={storageNodes}
            options={storagePlaceOptions}
            storageNodeId={formLot?.storageNodeId ?? null}
            slotIndex={formLot?.slotIndex ?? null}
            idPrefix={`reception-lot-${orderItemIndex}-${lotIndex}`}
            onChange={({ storageNodeId, slotIndex }) => {
              logInfo('OrderItemLotComponent', 'reception lot place chosen', {
                orderItemIndex,
                lotIndex,
                storageNodeId: storageNodeId ?? null,
                slotIndex: slotIndex ?? null,
              });
              formik.setFieldValue(
                `items[${orderItemIndex}].lotItems[${lotIndex}].storageNodeId`,
                storageNodeId
              );
              formik.setFieldValue(
                `items[${orderItemIndex}].lotItems[${lotIndex}].slotIndex`,
                slotIndex
              );
              onLotChange(orderItemIndex, 'storagePlace', {
                storageNodeId,
                slotIndex,
              }, lotIndex);
            }}
          />
        </Form.Group>
      ) : null}
      {!showRemoveButton && (
        <Button
          variant="outline-danger"
          onClick={() => {
            handleLotItemDelete(orderItemIndex, lotIndex);
          }}
        >
          <DeleteIcon />
        </Button>
      )}
    </div>
  );
};
export default OrderItemLotComponent;
